import React, { useState, useEffect } from "react";
import { postData } from "../../utility/Utility";
import Swal from "sweetalert2";
import CustomLoader from "../../Components/CustomLoader/CustomLoader";
import ReactPaginate from "react-paginate";
import GreetingCardModal from "../../Common/GreetingCardModal";

function GreetingCardList() {
    const [cards, setCards] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState("");
    const [currentPage, setCurrentPage] = useState(0);
    const [totalPages, setTotalPages] = useState(1);
    const [totalCards, setTotalCards] = useState(0);
    const [deleteLoading, setDeleteLoading] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedCard, setSelectedCard] = useState(null);
    const [editMode, setEditMode] = useState(false);

    // Fetch greeting cards from API
    const fetchCards = async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await postData("api/greeting-cards/list", {
                limit: 10,
                page: currentPage + 1, // API uses 1-based index
                search: searchTerm.trim()
            });

            if (response?.success) {
                setCards(response.data.cards || []);
                setTotalPages(response.data.pagination?.totalPages || 1);
                setTotalCards(response.data.pagination?.totalCount || 0);
            } else {
                setError(response?.message || "Failed to fetch greeting cards");
                setCards([]);
            }
        } catch (error) {
            console.error("Error fetching greeting cards:", error);
            setError("Error fetching greeting cards");
            setCards([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchCards();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage, searchTerm]);

    // Handle page change
    const handlePageChange = ({ selected }) => {
        setCurrentPage(selected);
    };

    // Handle form submit
    const handleSubmit = (e) => {
        e.preventDefault();
        setCurrentPage(0);
        fetchCards();
    };

    // Delete greeting card
    const handleDelete = async (cardId) => {
        const result = await Swal.fire({
            title: "Are you sure?",
            text: "You won't be able to revert this!",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#3085d6",
            cancelButtonColor: "#d33",
            confirmButtonText: "Yes, delete it!",
            cancelButtonText: "Cancel"
        });

        if (!result.isConfirmed) {
            return;
        }

        try {
            setDeleteLoading(cardId);
            const response = await postData("api/greeting-cards/delete", { cardId });

            if (response?.success) {
                Swal.fire({
                    title: "Deleted!",
                    text: "Greeting card has been deleted successfully.",
                    icon: "success",
                    timer: 2000,
                    showConfirmButton: false
                });
                // Refresh the list
                fetchCards();
            } else {
                Swal.fire({
                    title: "Error!",
                    text: response?.message || "Failed to delete greeting card",
                    icon: "error"
                });
            }
        } catch (error) {
            console.error("Error deleting greeting card:", error);
            Swal.fire({
                title: "Error!",
                text: "Error deleting greeting card",
                icon: "error"
            });
        } finally {
            setDeleteLoading(null);
        }
    };

    // Reset all filters
    const handleReset = () => {
        setSearchTerm("");
        setCurrentPage(0);
    };

    // Handle edit greeting card
    const handleEdit = (card) => {
        setSelectedCard(card);
        setEditMode(true);
        setIsModalOpen(true);
    };

 

    // Handle modal close
    const handleModalClose = () => {
        setIsModalOpen(false);
        setSelectedCard(null);
        setEditMode(false);
    };

    // Handle greeting card created/updated
    const handleCardSubmit = () => {
        // Refresh the list after card is created or updated
        fetchCards();
    };

    // Format date for display
    const formatDate = (dateString) => {
        if (!dateString) return "N/A";
        return new Date(dateString).toLocaleDateString('en-IN', {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
        });
    };

    if (loading) {
        return (
            <div className="content-page">
                <CustomLoader loading={true} />
            </div>
        );
    }

    if (error) {
        return (
            <div className="content-page">
                <div className="container">
                    <div className="row justify-content-center">
                        <div className="col-12">
                            <div className="alert alert-danger text-center">
                                <i className="mdi mdi-alert-circle me-2"></i>
                                {error}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="content-page">
            <CustomLoader loading={loading} />

            <div className="content">
                <div className="container-fluid">
                    {/* Header Section */}
                    <div className="py-3 d-flex align-items-sm-center flex-sm-row flex-column justify-content-between">
                        <div className="flex-grow-1">
                            <h4 className="fs-18 fw-semibold m-0">Greeting Card Management</h4>
                            <p className="text-muted mb-0">Total Cards: {totalCards}</p>
                        </div>

                   
                    </div>

                    {/* Search Section */}
                    <div className="row">
                        <div className="col-12">
                            <div className="card">
                                <div className="card-body">
                                    <form onSubmit={handleSubmit}>
                                        <div className="row g-3 align-items-end">
                                            <div className="col-md-12 col-lg-8">
                                                <label htmlFor="Search" className="form-label">Search</label>
                                                <div className="input-group">
                                                    <input
                                                        type="text"
                                                        className="form-control ps-4"
                                                        placeholder="Search by title or scheduled date..."
                                                        value={searchTerm}
                                                        onChange={(e) => setSearchTerm(e.target.value)}
                                                    />
                                                    <span className="input-group-text">
                                                        <i className="mdi mdi-magnify"></i>
                                                    </span>
                                                </div>
                                            </div>

                                            <div className="col-md-12 col-lg-4 d-flex gap-2 align-items-end">
                                                <button
                                                    type="submit"
                                                    className="btn btn-primary"
                                                    disabled={loading}
                                                >
                                                    {loading ? (
                                                        <span className="spinner-border spinner-border-sm me-1"></span>
                                                    ) : null}
                                                    Search
                                                </button>

                                                <button
                                                    type="button"
                                                    className="btn btn-secondary"
                                                    onClick={handleReset}
                                                    disabled={loading}
                                                >
                                                    Reset
                                                </button>
                                            </div>
                                        </div>
                                    </form>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Greeting Cards Table */}
                    <div className="row mt-2">
                        <div className="col-12">
                            <div className="card">
                                <div className="card-body">
                                    {loading ? (
                                        <div className="text-center py-5">
                                            <div className="spinner-border text-primary" role="status">
                                                <span className="visually-hidden">Loading...</span>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="table-responsive">
                                            <table className="table table-hover table-centered mb-0">
                                                <thead>
                                                    <tr>
                                                        <th>Title</th>
                                                        <th>Scheduled Date</th>
                                                        <th>Send Option</th>
                                                        <th>Image</th>
                                                        <th>Actions</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {cards?.length > 0 ? (
                                                        cards.map((card) => (
                                                            <tr key={card._id}>
                                                                <td>
                                                                    <div className="d-flex align-items-center">
                                                                        <div className="flex-shrink-0 me-2">
                                                                            <div className="avatar-xs">
                                                                                <div className="avatar-title rounded-circle bg-light text-primary">
                                                                                    {card.title?.charAt(0) || 'C'}
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                        <div className="flex-grow-1">
                                                                            <h5 className="fs-14 mb-1">{card.title}</h5>
                                                                            {card.message && (
                                                                                <small className="text-muted">
                                                                                    <i className="mdi mdi-message-text me-1"></i>

                                                                                    <span dangerouslySetInnerHTML={{ __html: card.message?.substring(0, 50) }} />
                                                                                </small>
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                </td>
                                                                <td>{formatDate(card.scheduledDate)}</td>
                                                                <td>
                                                                    <span className={`badge ${card.sendOption === 'email' ? 'bg-primary' : card.sendOption === 'sms' ? 'bg-info' : 'bg-success'}`}>
                                                                        {card.sendOption?.toUpperCase()}
                                                                    </span>
                                                                </td>
                                                                <td>
                                                                    {card.imageUrl ? (
                                                                        <img
                                                                            src={card.imageUrl}
                                                                            alt={card.title}
                                                                            style={{ width: '40px', height: '40px', objectFit: 'cover', borderRadius: '4px' }}
                                                                            onError={(e) => {
                                                                                e.target.style.display = 'none';
                                                                                e.target.nextSibling.style.display = 'block';
                                                                            }}
                                                                        />
                                                                    ) : (
                                                                        <div style={{ width: '40px', height: '40px', backgroundColor: '#f8f9fa', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                                            <i className="mdi mdi-image-off text-muted"></i>
                                                                        </div>
                                                                    )}
                                                                </td>
                                                           
                                                                <td>
                                                                    <div className="d-flex gap-2">
                                                                        <button
                                                                            className="btn btn-sm btn-primary"
                                                                            onClick={() => handleEdit(card)}
                                                                            title="Edit Greeting Card"
                                                                        >
                                                                            <i className="mdi mdi-pencil"></i> Edit
                                                                        </button>
                                                                        <button
                                                                            className="btn btn-sm btn-outline-danger"
                                                                            onClick={() => handleDelete(card._id)}
                                                                            disabled={deleteLoading === card._id}
                                                                            title="Delete Greeting Card"
                                                                        >
                                                                            {deleteLoading === card._id ? (
                                                                                <div className="spinner-border spinner-border-sm" role="status">
                                                                                    <span className="visually-hidden">Deleting...</span>
                                                                                </div>
                                                                            ) : (
                                                                                <i className="mdi mdi-delete"></i>
                                                                            )}
                                                                            Delete
                                                                        </button>
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                        ))
                                                    ) : (
                                                        <tr>
                                                            <td colSpan="7" className="text-center py-4">
                                                                No greeting cards found
                                                            </td>
                                                        </tr>
                                                    )}
                                                </tbody>
                                            </table>

                                            {/* Pagination */}
                                            {totalPages > 1 && (
                                                <ReactPaginate
                                                    previousLabel={<i className="mdi mdi-chevron-left"></i>}
                                                    nextLabel={<i className="mdi mdi-chevron-right"></i>}
                                                    breakLabel="..."
                                                    breakClassName="page-item"
                                                    breakLinkClassName="page-link"
                                                    pageCount={totalPages}
                                                    marginPagesDisplayed={2}
                                                    pageRangeDisplayed={5}
                                                    onPageChange={handlePageChange}
                                                    containerClassName="pagination pagination-rounded justify-content-end mt-3"
                                                    pageClassName="page-item"
                                                    pageLinkClassName="page-link"
                                                    previousClassName="page-item"
                                                    previousLinkClassName="page-link"
                                                    nextClassName="page-item"
                                                    nextLinkClassName="page-link"
                                                    activeClassName="active"
                                                    forcePage={currentPage}
                                                />
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Greeting Card Modal */}
            <GreetingCardModal
                isOpen={isModalOpen}
                onClose={handleModalClose}
                editMode={editMode}
                cardData={selectedCard}
                onCardCreated={handleCardSubmit}
                onCardUpdated={handleCardSubmit}
            />
        </div>
    );
}

export default GreetingCardList;
